/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import useDocumentsRecognize from '../common/hooks/useDocumentsRecognize';
import { createContext, useCallback, useState, useContext } from 'react';
import { isObjEmptyOrNull } from '../common/UtilityFunctions';
import { useLoanContext } from './LoanContext';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { useAuthContext } from './AuthContext';

type BorrowerEscrowAnalysisContextType = {
  loading: boolean;
  sortedAEADocs: DocumentRecognizeType[];
  errors: string;
};

const BorrowerEscrowAnalysisContext = createContext<BorrowerEscrowAnalysisContextType | undefined>(undefined);

const useBorrowerEscrowAnalysisContext = () => {
  const borrowerEscrowAnalysisContext = useContext(BorrowerEscrowAnalysisContext);
  if (borrowerEscrowAnalysisContext === undefined) {
    /// throwing this error helps in development when the context is out of scope.
    throw new Error('borrowerEscrowAnalysisContext must be inside a BorrowerEscrowAnalysisProvider');
  }
  return borrowerEscrowAnalysisContext;
};

const BorrowerEscrowAnalysisProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getDocumentInfo, errors } = useDocumentsRecognize();
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();

  const getSortedAEALettersAvailable = useCallback(async (): Promise<DocumentRecognizeType[]> => {
    var params = {
      subcategory: 2,
      active: true,
    };
    const res = await getDocumentInfo(params, 'ESCROW_ANALYSIS_LETTERS');
    if (!isObjEmptyOrNull(res)) {
      let finalArr = res.filter((doc) => doc.active);
      finalArr.sort((a, b) => a.dateCreated - b.dateCreated);
      return finalArr;
    } else {
      return [];
    }
  }, [getDocumentInfo]);

  const {
    data: sortedAEADocs,
    isFetching: fetchingSortedAEADocs,
    isLoading: loadingSortedAEADocs,
  } = useQuery({
    queryKey: [loanNumber, token, 'sortedAEALetters'],
    queryFn: ({ signal }: QueryFunctionContext) => getSortedAEALettersAvailable(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
  });

  const loading = fetchingSortedAEADocs || loadingSortedAEADocs;

  return (
    <BorrowerEscrowAnalysisContext.Provider value={{ loading, sortedAEADocs, errors }}>
      {children}
    </BorrowerEscrowAnalysisContext.Provider>
  );
};

export { BorrowerEscrowAnalysisProvider, useBorrowerEscrowAnalysisContext };
